/**
 * MermaidRenderer Component
 * Renders Mermaid diagrams with Shell branding and error handling
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';

import { diagramService } from '../../services/diagramService';
import { isChunkLoadError } from '../../utils/chunkReload';
import { logger } from '../../utils/logger';
import { getMermaidDiagramType, validateMermaidSyntax } from '../../utils/mermaidConfig';
import {
  cleanupMermaidErrorElements,
  renderMermaidDiagram,
} from '../../utils/mermaidManager';

export interface MermaidRendererProps {
  /** Mermaid diagram code to render */
  code: string;
  /** Theme variant - light or dark */
  theme?: 'light' | 'dark';
  /** Additional CSS classes */
  className?: string;
  /** Unique identifier for the diagram */
  id?: string;
  /** Callback when diagram renders successfully */
  onRender?: (diagramType: string) => void;
  /** Callback when rendering fails */
  onError?: (error: string) => void;
  /** Maximum width for the diagram */
  maxWidth?: string;
  /** Maximum height for the diagram */
  maxHeight?: string;
  /** Enable zoom controls */
  enableZoom?: boolean;
  /** Enable pan functionality */
  enablePan?: boolean;
  /** Initial zoom level */
  initialZoom?: number;
  /** Minimum zoom level */
  minZoom?: number;
  /** Maximum zoom level */
  maxZoom?: number;
  /** Callback with fixed code when Archie successfully fixes the diagram */
  onFixCode?: (fixedCode: string) => void;
  /** Architecture section key forwarded to the fix API for agent-specific guidelines */
  section?: string;
}

/**
 * Apply standard style and ARIA attributes to a rendered SVG element
 * to ensure it is responsive and accessible.
 */
function applySvgAttributes(
  svgElement: SVGSVGElement,
  diagramType: string,
): void {
  svgElement.style.maxWidth = 'none';
  svgElement.style.maxHeight = 'none';
  svgElement.style.height = 'auto';
  svgElement.style.width = 'auto';
  svgElement.style.display = 'block';
  svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
  if (!svgElement.getAttribute('role')) {
    svgElement.setAttribute('role', 'img');
  }
  if (!svgElement.getAttribute('aria-label')) {
    svgElement.setAttribute('aria-label', `${diagramType} diagram`);
  }
}

/**
 * Remove Mermaid's temporary render element for a specific
 * diagram ID.  Does NOT do a broad sweep — that would
 * destroy temp elements belonging to other in-flight renders.
 */
function cleanupOrphanMermaidElements(
  renderDiagramId: string,
): void {
  if (renderDiagramId) {
    // A failed render (plus its fallback) can append more than one
    // orphan error element with the same id="d{id}" to document.body.
    // getElementById only finds the first, so loop until all are gone.
    let orphan = document.getElementById(`d${renderDiagramId}`);
    while (orphan) {
      orphan.remove();
      orphan = document.getElementById(`d${renderDiagramId}`);
    }
  }
}

/**
 * MermaidRenderer component that renders Mermaid diagrams with Shell branding
 */
export const MermaidRenderer: React.FC<MermaidRendererProps> = ({
  code,
  theme = 'light',
  className = '',
  id,
  onRender,
  onError,
  maxWidth = '70%',
  onFixCode,
  section,
  maxHeight = '100%',
  enableZoom = true,
  enablePan = true,
  initialZoom = 0.8,
  minZoom = 0.1,
  maxZoom = 5,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const svgContainerRef = useRef<HTMLDivElement>(null);
  const diagramIdRef = useRef<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [isFixing, setIsFixing] = useState(false);
  const [fixError, setFixError] = useState<string | null>(null);
  const [renderAttempt, setRenderAttempt] = useState(0);
  const [zoom, setZoom] = useState(initialZoom);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Counter to detect stale renders: when a new render starts,
  // the counter increments and any prior in-flight render becomes
  // a no-op once it resumes after the await.
  const renderGenRef = useRef(0);

  // Generate unique diagram ID — always append a unique
  // suffix even when an `id` prop is provided so that two
  // concurrent mermaid.render() calls never share the same
  // temporary DOM element (which causes the "element.
  // firstChild" crash).
  const generateDiagramId = useCallback(() => {
    const base36 = 36;
    const suffix = `${Date.now()}-${Math.random().toString(base36).substr(2, 9)}`;
    return id ? `${id}-${suffix}` : `mermaid-${suffix}`;
  }, [id]);

  // Zoom functionality
  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev * 1.2, maxZoom));
  }, [maxZoom]);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev / 1.2, minZoom));
  }, [minZoom]);

  const handleResetZoom = useCallback(() => {
    setZoom(initialZoom);
    setPan({ x: 0, y: 0 });
  }, [initialZoom]);

  const handleFitToView = useCallback(() => {
    if (!svgContainerRef.current) {return;}
    
    const container = svgContainerRef.current;
    const svg = container.querySelector('svg');
    if (!svg) {return;}

    const containerRect = container.getBoundingClientRect();
    const svgRect = svg.getBoundingClientRect();
    
    const scaleX = containerRect.width / svgRect.width;
    const scaleY = containerRect.height / svgRect.height;
    const scale = Math.min(scaleX, scaleY, 1);
    
    setZoom(scale);
    setPan({ x: 0, y: 0 });
  }, []);

  // Pan functionality
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!enablePan) {return;}
    setIsDragging(true);
    setDragStart({ x: e.clientX - pan.x, y: e.clientY - pan.y });
    e.preventDefault();
  }, [enablePan, pan.x, pan.y]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDragging || !enablePan) {return;}
    setPan({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  }, [isDragging, enablePan, dragStart.x, dragStart.y]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Wheel zoom
  const handleWheel = useCallback((e: React.WheelEvent) => {
    if (!enableZoom || !e.ctrlKey) {return;}
    e.preventDefault();
    
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setZoom(prev => Math.max(minZoom, Math.min(maxZoom, prev * delta)));
  }, [enableZoom, minZoom, maxZoom]);

  // Effect to render diagram when code or theme changes.
  // Uses an `aborted` flag so that when the effect re-fires (e.g. during
  // regeneration) the previous in-flight render is silently discarded
  // instead of racing with the new one.
  useEffect(() => {
    let aborted = false;
    // Watchdog against a render that never resolves OR rejects — most often a
    // dynamic import() of mermaid that stalls (a bad chunk fetch), leaving the
    // try/finally hung so setIsLoading(false) never runs and the
    // "Rendering diagram..." spinner spins forever. Healthy diagrams render in
    // well under a second, so in practice this only ever fires for a genuine
    // hang, at which point we surface the inline error banner (Retry / Ask
    // Archie) instead of an endless spinner.
    const RENDER_WATCHDOG_MS = 20000;
    let watchdogId: ReturnType<typeof setTimeout> | null = null;

    const handleRenderError = (err: unknown, renderDiagramId: string) => {
      if (aborted) {return;}

      const rawMessage =
        err instanceof Error
          ? err.message
          : 'Failed to render diagram';
      // A failed dynamic import means a new build was deployed and this
      // tab references stale chunks. Archie cannot "fix" that, so show a
      // refresh hint instead of the diagram error modal; the global
      // handler reloads the page to fetch the latest assets.
      const chunkError = isChunkLoadError(rawMessage);
      const errorMessage = chunkError
        ? 'A new version of Archie was deployed. Refreshing to load the '
          + 'latest diagram components…'
        : rawMessage;
      setError(errorMessage);
      setShowErrorModal(!chunkError);
      onError?.(errorMessage);

      logger.info('Error:', err);
      logger.info('Code:', code);
      logger.info('Theme:', theme);
      logger.info(
        'Diagram Type:',
        getMermaidDiagramType(code),
      );

      if (containerRef.current) {
        containerRef.current.innerHTML = '';
      }
      cleanupOrphanMermaidElements(renderDiagramId);
    };

    const run = async () => {
      if (!containerRef.current || !code.trim()) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);
      watchdogId = setTimeout(() => {
        if (aborted) {
          return;
        }
        setIsLoading(false);
        setError('Diagram render timed out');
        setShowErrorModal(false);
      }, RENDER_WATCHDOG_MS);

      let renderDiagramId = '';

      try {
        const validation = validateMermaidSyntax(code);
        if (!validation.isValid) {
          throw new Error(
            validation.error ?? 'Invalid Mermaid syntax',
          );
        }

        renderDiagramId = generateDiagramId();
        diagramIdRef.current = renderDiagramId;

        const container = containerRef.current;
        if (container) {
          container.innerHTML = '';
        }

        const { svg } = await renderMermaidDiagram(
          renderDiagramId,
          code,
          theme === 'dark',
        );

        // If the effect was cleaned up while we awaited, discard
        if (aborted) {return;}

        if (container && svg) {
          container.innerHTML = svg;
          const svgElement = container.querySelector('svg');
          if (svgElement) {
            applySvgAttributes(
              svgElement as SVGSVGElement,
              getMermaidDiagramType(code),
            );
          }
        }

        const diagramType = getMermaidDiagramType(code);
        onRender?.(diagramType);
      } catch (err) {
        handleRenderError(err, renderDiagramId);
      } finally {
        if (watchdogId) {
          clearTimeout(watchdogId);
          watchdogId = null;
        }
        if (!aborted) {
          setIsLoading(false);
        }
      }
    };

    run();

    return () => {
      aborted = true;
      if (watchdogId) {
        clearTimeout(watchdogId);
      }
      cleanupMermaidErrorElements(diagramIdRef.current);
    };
  }, [code, theme, generateDiagramId, onRender, onError, maxWidth, maxHeight, renderAttempt]);

  return (
    <div className={`mermaid-renderer ${className}`}>
      {isLoading && (
        <div className="flex items-center justify-center p-4">
          <span className="loading loading-spinner loading-md"></span>
          <span className="ml-2">Rendering diagram...</span>
        </div>
      )}

      <div
        ref={containerRef}
        className={`mermaid-container ${isLoading ? 'opacity-50' : ''}`}
        style={{
          minHeight: isLoading ? '100px' : 'auto',
          maxWidth,
          maxHeight,
        }}
        aria-label={`Mermaid diagram: ${getMermaidDiagramType(code)}`}
      />

      {/* Inline error banner — persists after the modal is dismissed so the
         diagram area is never silently blank.  Offers Retry and Ask Archie. */}
      {!isLoading && error && !showErrorModal && (
        <div className="flex items-center gap-2 p-3 my-2 rounded-lg bg-error/10 border border-error/30 text-sm">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="stroke-error h-5 w-5 shrink-0"
            fill="none"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="flex-1 truncate text-error">
            {fixError ?? 'Diagram failed to render'}
          </span>
          <button
            className="btn btn-xs btn-outline btn-error"
            onClick={() => {
              setError(null);
              setFixError(null);
              setRenderAttempt(prev => prev + 1);
            }}
          >
            Retry
          </button>
          {onFixCode && (
            <button
              className="btn btn-xs btn-primary"
              disabled={isFixing}
              onClick={async () => {
                setIsFixing(true);
                setFixError(null);
                try {
                  const fixedCode = await diagramService.fixMermaidDiagram(
                    code,
                    error ?? '',
                    section,
                  );
                  onFixCode(fixedCode);
                  setError(null);
                } catch {
                  setFixError(
                    'Archie could not fix this diagram — try regenerating the section',
                  );
                } finally {
                  setIsFixing(false);
                }
              }}
            >
              {isFixing ? (
                <span className="loading loading-spinner loading-xs" />
              ) : null}
              Ask Archie
            </button>
          )}
          <button
            className="btn btn-xs btn-ghost"
            onClick={() => setShowErrorModal(true)}
          >
            Details
          </button>
        </div>
      )}

      {/* Accessibility: Provide text alternative */}
      <div className="sr-only">
        Mermaid diagram code: {code}
      </div>

      {/* Error popup modal — shown when the diagram fails to render */}
      {showErrorModal && error && (
        <dialog className="modal modal-open">
          <div className="modal-box max-w-lg">
            <div className="flex items-start gap-3">
              <div className="shrink-0 mt-0.5">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="stroke-error h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9
                       9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-bold text-error mb-1">Diagram Rendering Error</h3>
                <p className="text-sm opacity-80 mb-3">{error}</p>
                {fixError && (
                  <p className="text-sm text-warning mb-3">{fixError}</p>
                )}
                <details className="collapse collapse-arrow bg-base-200 rounded">
                  <summary className="collapse-title text-xs font-medium py-2 min-h-0">
                    Show diagram source
                  </summary>
                  <div className="collapse-content">
                    <pre className="text-xs p-2 overflow-auto max-h-40 whitespace-pre-wrap break-all">
                      {code}
                    </pre>
                  </div>
                </details>
              </div>
            </div>
            <div className="modal-action mt-4">
              {onFixCode && (
                <button
                  className="btn btn-sm btn-primary"
                  disabled={isFixing}
                  onClick={async () => {
                    setIsFixing(true);
                    setFixError(null);
                    try {
                      const fixedCode = await diagramService.fixMermaidDiagram(
                        code,
                        error ?? '',
                        section,
                      );
                      onFixCode(fixedCode);
                      setShowErrorModal(false);
                    } catch {
                      setFixError(
                        'Archie could not fix this diagram — try regenerating the section',
                      );
                    } finally {
                      setIsFixing(false);
                    }
                  }}
                >
                  {isFixing ? (
                    <span className="loading loading-spinner loading-xs"></span>
                  ) : null}
                  Ask Archie
                </button>
              )}
              <button
                className="btn btn-sm btn-ghost"
                onClick={() => setShowErrorModal(false)}
              >
                Dismiss
              </button>
            </div>
          </div>
          <form
            method="dialog"
            className="modal-backdrop"
            onSubmit={() => setShowErrorModal(false)}
          >
            <button>close</button>
          </form>
        </dialog>
      )}
    </div>
  );
};

export default MermaidRenderer;
